import React, { useState } from 'react';
import axios from 'axios';
import { X, Send } from 'lucide-react';
import TaskStatusBadge from './TaskStatusBadge';
import { useApiHandler } from '../../../utils/apiHandler';

function TaskModal({ task, isOpen, onClose }) {
  const [newComment, setNewComment] = useState('');
  const [taskStatus, setTaskStatus] = useState(task.status || 'Not Started');
  const [comments, setComments] = useState(task.comments || []);
  const apiHandler = useApiHandler();

  // Handle status change (no API call here, just update local state)
  const handleStatusChange = (newStatus) => {
    setTaskStatus(newStatus); // Update the status locally
  };

  // Handle comment submit (this is where the API call happens)
  const handleCommentSubmit = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return; // If no comment, do not proceed

    try {
      // Prepare body to send both status and comment together
      const body = {
        status: taskStatus, // Keep the current status
        comment: newComment.trim(), // Add the new comment
      };

      // Make a single API call to update status and add comment
      const response = await apiHandler.put(
        `/employee/employee-task/${task._id}`,
        body
      );

      if (response.status === 200) {
        // Update comments locally
        setComments((prevComments) => [
          ...prevComments,
          { user: 'Employee', text: body.comment, createdAt: new Date().toISOString() },
        ]);

        // Clear the comment input field
        setNewComment('');
      } else {
        console.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-lg font-semibold">{task.title || 'Task Details'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{task.taskDetails || 'No task details provided.'}</p>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Status:</span>
              <div className="flex space-x-2">
                {['Not Started', 'In Progress', 'Completed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)} // Change status locally
                    className={`px-3 py-1 rounded-full text-xs ${
                      taskStatus === status ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <TaskStatusBadge status={status} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="font-medium mb-2">Comments</h3>
            <div className="space-y-4 mb-4">
              {comments.map((comment) => (
                <div key={comment.id || comment._id} className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{comment.userName || ' Employee'}</span>
                    <span className="text-gray-500">{new Date(comment.createdAt).toLocaleString() || 'No timestamp'}</span>
                  </div>
                  <p className="text-sm mt-1">{comment.text || 'No comment text provided.'}</p>
                </div>
              ))}
            </div>

            <form onSubmit={handleCommentSubmit} className="flex items-center space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="submit"
                className="bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TaskModal;
